import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Sign in, reset your password, and recover account access | Paddiq';

export function AccountAccessPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Account access guide</p>
            <h1>Sign in and recover access to Paddiq.</h1>
            <p className="guide-hero__lead">
              Choose the right sign-in method, reset a forgotten password, change a password you
              know, or get help with an expired account setup link.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/login')}
              >
                Open the login screen
              </a>
              <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                Contact support
              </a>
            </div>
            <p className="guide-hero__meta">Web and mobile access · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/account-access" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#sign-in">Choose how to sign in</a>
                </li>
                <li>
                  <a href="#reset">Reset a forgotten password</a>
                </li>
                <li>
                  <a href="#change">Change a known password</a>
                </li>
                <li>
                  <a href="#setup-link">Finish account setup</a>
                </li>
                <li>
                  <a href="#troubleshooting">Troubleshoot access</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Keep links private</strong>
                <span>
                  Never send anyone your password, password-reset link, or account setup link.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="sign-in">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Login</p>
                  <h2>Use the sign-in method for your account.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open the Paddiq <strong>Login</strong> tab.
                    </li>
                    <li>
                      Enter your <strong>Email or username</strong> and password, then select{' '}
                      <strong>Sign in</strong>.
                    </li>
                    <li>
                      If you normally use Google or Apple, select the same option instead of
                      entering a Paddiq password.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    An account created from a team setup link is not ready for normal sign-in until
                    the recipient opens that link and chooses a password.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="reset">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Forgotten password</p>
                  <h2>Request a password-reset email.</h2>
                  <ol className="guide-instructions">
                    <li>
                      On the login screen, select <strong>Forgot password?</strong>
                    </li>
                    <li>
                      Enter the email address attached to the account and select{' '}
                      <strong>Send reset link</strong>.
                    </li>
                    <li>Open the email and select the link within one hour.</li>
                    <li>
                      Enter and confirm a password of at least eight characters, then select{' '}
                      <strong>Update password</strong>.
                    </li>
                  </ol>
                  <p>
                    Paddiq shows the same confirmation whether or not an account exists for that
                    email. This keeps account information private.
                  </p>
                </div>
              </section>

              <figure className="guide-workflow guide-workflow--auth">
                <img
                  src="/account-password-reset-form.webp"
                  width="460"
                  height="410"
                  alt="Paddiq Forgot password form with an Email field, Send reset link button, and Back to sign in button"
                />
                <figcaption>
                  The reset form asks only for the account email. No password or team information
                  belongs in this form.
                </figcaption>
              </figure>

              <section className="guide-step" id="change">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Known password</p>
                  <h2>Change your password while signed in.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Settings</strong>, then choose <strong>Password</strong>.
                    </li>
                    <li>Enter your current password.</li>
                    <li>Enter and confirm a new password of at least eight characters.</li>
                    <li>
                      Select <strong>Update password</strong>.
                    </li>
                  </ol>
                  <p>
                    The updated password is used for password sign-in on both web and mobile. If you
                    cannot provide the current password, use the login-screen reset instead.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="setup-link">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Invited account</p>
                  <h2>Finish an account created for you.</h2>
                  <ol className="guide-instructions">
                    <li>Open the Paddiq account setup email you received.</li>
                    <li>Use the setup link before the expiration date shown in the email.</li>
                    <li>Choose and confirm a password of at least eight characters.</li>
                    <li>
                      Select <strong>Finish registration</strong>. Paddiq signs you in when setup
                      succeeds.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    If the setup link is invalid or expired, do not create a duplicate account.
                    Contact the workspace owner or Paddiq support to restore access to the intended
                    account.
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If you still cannot sign in</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>The reset email did not arrive</h3>
                    <p>
                      Check spam or junk mail, confirm the account email, then submit the form
                      again. A driver username can work at login, but the reset form requires an
                      email address.
                    </p>
                  </article>
                  <article>
                    <h3>The reset link expired</h3>
                    <p>
                      Return to <strong>Forgot password?</strong> and request a new link. Do not
                      reuse or forward the expired link.
                    </p>
                  </article>
                  <article>
                    <h3>You normally use Google or Apple</h3>
                    <p>
                      Return to the login screen and use the same option. A Paddiq password-reset
                      email may not be available for an account that uses only Google or Apple.
                    </p>
                  </article>
                  <article>
                    <h3>Your account setup link expired</h3>
                    <p>
                      Contact the workspace owner or{' '}
                      <a className="guide-text-link" href="/support">
                        Paddiq support
                      </a>
                      . Include the account email or username, team name, and exact error—never the
                      setup link or a password.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Return to Paddiq</p>
                  <h2>Use the right recovery path for your account.</h2>
                  <p>
                    Start at the login screen. If the normal reset does not fit your account, send
                    support the account details and the error shown.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/login')}
                  >
                    Open the login screen
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
